import { useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import type { DataTableFilterOption } from '@r/types/index.ts';
import { ArrowDownUpIcon, ChevronDownIcon, PlusIcon, TextIcon } from 'lucide-react';

import { Button } from '@r/components/ui/button.tsx';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@r/components/ui/command.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@r/components/ui/popover.tsx';

interface DataTableFilterComboboxProps<TData> {
  options: DataTableFilterOption<TData>[];
  selectedOptions: DataTableFilterOption<TData>[];
  setSelectedOptions: Dispatch<SetStateAction<DataTableFilterOption<TData>[]>>;
  onSelect: () => void;
  children?: ReactNode;
}

export function DataTableFilterCombobox<TData>({
  options,
  selectedOptions,
  setSelectedOptions,
  onSelect,
  children
}: DataTableFilterComboboxProps<TData>) {
  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<DataTableFilterOption<TData>>(
    options[0] ?? ({} as DataTableFilterOption<TData>)
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children ?? (
          <Button variant="outline" size="custom_sm" role="combobox" className="capitalize">
            <ArrowDownUpIcon className="mr-2 size-4 shrink-0" aria-hidden="true" />
            Filter
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[12.5rem] p-0" align="end">
        <Command>
          <CommandInput placeholder="Filter by..." />
          <CommandList>
            <CommandEmpty>No item found.</CommandEmpty>
            <CommandGroup>
              {options
                .filter(
                  (option) =>
                    !selectedOptions.some((selectedOption) => selectedOption.value === option.value)
                )
                .map((option) => (
                  <CommandItem
                    key={String(option.value)}
                    className="capitalize"
                    value={String(option.value)}
                    onSelect={(currentValue) => {
                      setValue(currentValue === value ? '' : currentValue);
                      setOpen(false);
                      setSelectedOption(option);
                      setSelectedOptions((prev) => {
                        return [...prev, { ...option }];
                      });
                      onSelect();
                    }}
                  >
                    {option.options.length > 0 ? (
                      <ChevronDownIcon className="mr-2 size-4" aria-hidden="true" />
                    ) : (
                      <TextIcon className="mr-2 size-4" aria-hidden="true" />
                    )}
                    {option.label}
                  </CommandItem>
                ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  setSelectedOptions([
                    ...selectedOptions,
                    {
                      id: crypto.randomUUID(),
                      label: selectedOption?.label ?? '',
                      value: selectedOption?.value ?? '',
                      options: selectedOption?.options ?? [],
                      isMulti: true
                    }
                  ]);
                  onSelect();
                }}
              >
                <PlusIcon className="mr-2 size-4" aria-hidden="true" />
                Advanced filter
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
